import { useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./operating-theatre.module.css";
import {
  useGetOperatingTheatreDetailsQuery,
  useUpdateOperatingTheatreActivationMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { RootState, useAppSelector } from "../../store";
import { colors } from "../../theme/colors";

/**
 * Displays details of a single Operating Theatre including deactivation metadata when available.
 *
 * Returns:
 *   JSX.Element: Detail view for the selected operating theatre.
 *
 * Notes:
 *   Requires an id route param; fetches details via RTK Query with skip when id is absent.
 */
const OperatingTheatreDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userDetails } = useAppSelector((state: RootState) => state.auth);

  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: operatingTheatreDetails,
    isLoading,
    isFetching,
  } = useGetOperatingTheatreDetailsQuery(id ?? "", {
    skip: !id,
  });
  const [updateOperatingTheatreActivation] =
    useUpdateOperatingTheatreActivationMutation();

  const handleActivate = async () => {
    if (!operatingTheatreDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateOperatingTheatreActivation({
        id: operatingTheatreDetails.id,
        is_active: true,
        deactivate_reason: "",
        health_org_id: userDetails?.health_org_id ?? 0,
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            <Typography className={style.operatingTheatreDetailHeading}>
              Operating Theatre Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {operatingTheatreDetails?.name}
                </Typography>
              </Box>

              {(operatingTheatreDetails?.room_no ||
                operatingTheatreDetails?.floor ||
                operatingTheatreDetails?.building) && (
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Theatre Location
                    </Typography>
                    <Typography className={style.value}>
                      {`${operatingTheatreDetails?.room_no
                        ? operatingTheatreDetails?.room_no +
                        (operatingTheatreDetails?.floor ? "," : "")
                        : ""
                        } ${operatingTheatreDetails?.floor
                          ? operatingTheatreDetails?.floor +
                          (operatingTheatreDetails?.building ? "," : "")
                          : ""
                        } ${operatingTheatreDetails?.building ?? ""}`}
                    </Typography>
                  </Box>
                )}

              {operatingTheatreDetails?.deactivate_reason && (
                <Box>
                  <Typography className={style.operatingTheatreDetailHeading}>
                    Deactivation Details
                  </Typography>
                  <Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Reason for deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {operatingTheatreDetails?.deactivate_reason}
                      </Typography>
                    </Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Date of Deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {operatingTheatreDetails?.deactivated_at
                          ? dayjs(
                            operatingTheatreDetails?.deactivated_at
                          ).format("MMMM DD YYYY, hh:mm:ss A")
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {message && (
                <Box mt={2}>
                  <LabelBox
                    boxProps={{
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                    }}
                    data={message}
                    onClose={() => setMessage(null)}
                  />
                </Box>
              )}
              <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  label={"Close"}
                />
                {operatingTheatreDetails?.is_active ? (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Edit"}
                    onClick={() => {
                      navigate(`/operating-theatres/edit/${id}`);
                    }}
                  />
                ) : (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Activate"}
                    onClick={() => setActivateModalVisible(true)}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${operatingTheatreDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default OperatingTheatreDetails;
